import React from 'react';
import { get } from 'lodash';
import * as Yup from 'yup';

import Grid from '~/components/core/Atomic/Grid/Grid';
import { useCms } from '~/components/hooks/useCms';
import useSafeFormikContext from '~/components/hooks/useSafeFormikContext';
import {
  activeFieldGroups,
  emptyFieldGroupFormikValues,
  fieldGroupValidations,
} from '~/components/IncidentConfiguration/HierarchyFieldGroup/hierarchyTree';
import type { FieldGroup } from '~/components/types/incident-configuration-types';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import { useIncidentConfiguration } from '../../../../../hooks/useIncidentConfiguration';
import ConfiguredFields, {
  getConfiguredFieldsEmptyFormikInitialValues,
  getConfiguredFieldsValidations,
} from '../../../../../IncidentConfiguration/ConfiguredFields';
import SectionFieldGroupPickers from '../../SectionFieldGroupPickers';

// Admin-authored supplementary sections live under this config key.
const ADDITIONAL_INFORMATION_SECTIONS_CONFIG_PATH = 'incident_details.additional_information_sections';
const CONFIGURED_FIELDS_PATH = 'incident_details.configured_fields';

// The generated API contracts do not carry `additional_information_sections` yet, so these shapes are
// declared locally rather than imported from server_shared. Only the properties this module actually
// reads are modelled — widening them is the job of whoever regenerates the contracts.
interface ConfiguredField {
  id?: string;
  desc?: string;
  type?: string;
  section?: string;
  active?: boolean;
  mandatory?: boolean;
  incident_sub_type_categories?: string[];
}

interface AdditionalInformationSection {
  id: string;
  desc?: string;
  active?: boolean;
  mandatory?: boolean;
  incident_sub_type_categories?: string[];
}

// The sub-type predicate is object-agnostic on purpose: it reads `incident_sub_type_categories` off
// whatever it is handed, so the same function scopes a section, a field and a field group.
type SubtypeOmitFn = (candidate: ConfiguredField | AdditionalInformationSection | FieldGroup | undefined) => boolean;

type IncidentConfiguration = Record<string, unknown> | undefined | null;

// A single frozen empty array so an org with no custom sections always hands back the same reference
// instead of allocating a fresh [] on every call.
const EMPTY: readonly never[] = Object.freeze([]);

// Each custom section stores its dialog values under incident_details.<id>, mirroring
// supplementary_information's 'incident_details.supplementary_information' path.
const getCustomSectionPath = (sectionId: string): string => `incident_details.${sectionId}`;

// Sections without an id are dropped at the source: an id-less section's field scan would match every
// untagged Incident Details field (field.section === undefined) and its dialog would save to
// incident_details.undefined, so nothing downstream should ever see one. The original array is returned
// untouched when every section is valid, so callers keep a stable reference in the common case.
const getCustomAdditionalInformationSections = (
  incidentConfiguration: IncidentConfiguration
): readonly AdditionalInformationSection[] => {
  const sections =
    (get(incidentConfiguration, ADDITIONAL_INFORMATION_SECTIONS_CONFIG_PATH) as
      | readonly AdditionalInformationSection[]
      | undefined) ?? (EMPTY as readonly AdditionalInformationSection[]);
  return sections.some((section) => !section?.id) ? sections.filter((section) => section?.id) : sections;
};

const getConfiguredFieldsForSection = (
  incidentConfiguration: IncidentConfiguration,
  sectionId: string | undefined
): readonly ConfiguredField[] => {
  if (!sectionId) {
    return EMPTY;
  }
  const fields =
    (get(incidentConfiguration, CONFIGURED_FIELDS_PATH) as readonly ConfiguredField[] | undefined) ?? EMPTY;
  return fields.filter((field) => field?.section === sectionId);
};

// The hierarchy groups a section renders. The section id is guarded like getConfiguredFieldsForSection's:
// a missing section has no groups of its own. The feature flag is read by the calling
// component, since every helper here is a function of the configuration only.
const getFieldGroupsForSection = (
  incidentConfiguration: IncidentConfiguration,
  sectionId: string | undefined,
  isFieldSupportedBySubtype: SubtypeOmitFn | undefined,
  isHierarchyFieldGroupsEnabled: boolean
): FieldGroup[] =>
  isHierarchyFieldGroupsEnabled && sectionId
    ? activeFieldGroups(
        // This module types the configuration loosely and reads it through lodash `get`. activeFieldGroups
        // reads only incident_details.field_groups and treats a missing one as none, so the cast is safe.
        incidentConfiguration as Parameters<typeof activeFieldGroups>[0],
        (fieldGroup) => isFieldSupportedBySubtype?.(fieldGroup) ?? true,
        sectionId
      )
    : [];

// Mirrors getIsSupplementaryInformationSectionActive's field scan: a custom section renders on the
// FNOL form only when it is shown (active) AND holds at least one active/mandatory configured field or
// hierarchy group.
const getIsCustomSectionActive = (
  incidentConfiguration: IncidentConfiguration,
  section: AdditionalInformationSection | undefined,
  isHierarchyFieldGroupsEnabled: boolean
): boolean =>
  Boolean(section?.active) &&
  (getConfiguredFieldsForSection(incidentConfiguration, section?.id).some(
    (field) => field?.active || field?.mandatory
  ) ||
    getFieldGroupsForSection(incidentConfiguration, section?.id, undefined, isHierarchyFieldGroupsEnabled).length > 0);

// A custom section is effectively mandatory (blocks submit until verified) when the claim's sub-type is
// inside the SECTION's own category scope AND either its `mandatory` flag is set or it holds a field that
// is active, mandatory and kept for this sub-type. The predicate matches exactly what CustomSectionFragment
// renders (active + the sub-type omit fn) — there is no geo clause because, unlike getInitialSectionConfig's
// pre-defined-field scan, the configured-field render path does not region-filter (ConfiguredFields drops
// only active === false). The predicate is guarded like the sibling so an omitted argument degrades to
// "not promoted" rather than throwing.
//
// The section-level scope check is the outermost term on purpose. A section the claim's sub-type excludes
// holds no fields this claim can fill, so it must never demand verification — product ruling: a claim
// outside a section's category is not stopped by it. Both inner terms have to sit behind it: `mandatory`
// short-circuits the field scan, so guarding only the scan would still promote a Required section, and a
// mandatory field carrying no categories of its own passes the sub-type predicate on its own terms even
// when its section is excluded. Scoping is read off the section object exactly as the claim-side sibling
// reads it, rather than reaching into incident_sub_type_categories here.
//
// This governs only the mandatory/verified decision. Whether the section RENDERS is getIsCustomSectionActive,
// which is deliberately left category-blind: an excluded section stays visible-but-disabled (the disabled
// state comes from AdditionalInformationSection's own isFieldSupportedBySubtype(sectionConfig) check), and
// that is a different behaviour from the Show=off and zero-field cases, which remove the row entirely.
// This split — display on `active`, disabled on the sub-type predicate — is the convention the auto
// sections already follow in IncidentMoreActionsContainer.
//
// A mandatory hierarchy group the claim's sub-type keeps promotes the section exactly like a mandatory field.
const getIsCustomSectionMandatory = (
  incidentConfiguration: IncidentConfiguration,
  section: AdditionalInformationSection | undefined,
  isFieldSupportedBySubtype: SubtypeOmitFn | undefined,
  isHierarchyFieldGroupsEnabled: boolean
): boolean =>
  Boolean(isFieldSupportedBySubtype?.(section)) &&
  (Boolean(section?.mandatory) ||
    getConfiguredFieldsForSection(incidentConfiguration, section?.id).some(
      (field) => field?.active && field?.mandatory && isFieldSupportedBySubtype?.(field)
    ) ||
    getFieldGroupsForSection(
      incidentConfiguration,
      section?.id,
      isFieldSupportedBySubtype,
      isHierarchyFieldGroupsEnabled
    ).some((fieldGroup) => fieldGroup.mandatory));

// A custom section's menu row is enabled only when the claim's sub-type keeps at least one of its fields
// renderable. The predicate matches what ConfiguredFields actually renders — a field survives when it is
// not explicitly inactive (`active !== false`) AND passes the sub-type filter — so the section-level check
// alone can no longer leave a row that opens an empty dialog when every field is filtered out.
// Does this claim's sub-type leave the section with anything to show? Matches what ConfiguredFields
// actually renders — a field survives when it is not explicitly inactive (`active !== false`) AND passes
// the sub-type filter. Split out from getIsCustomSectionEnabled because the two surfaces need different
// halves: the FNOL form must NOT consider the section-level category check here, since a
// category-excluded section is deliberately visible-but-disabled there (see getIsCustomSectionMandatory).
// A hierarchy group the sub-type keeps counts as something to show, as a field does.
const getHasRenderableFieldForSubtype = (
  incidentConfiguration: IncidentConfiguration,
  section: AdditionalInformationSection | undefined,
  additionalFieldOmitFn: SubtypeOmitFn,
  isHierarchyFieldGroupsEnabled: boolean
): boolean =>
  getConfiguredFieldsForSection(incidentConfiguration, section?.id).some(
    (field) => field?.active !== false && additionalFieldOmitFn(field)
  ) ||
  getFieldGroupsForSection(incidentConfiguration, section?.id, additionalFieldOmitFn, isHierarchyFieldGroupsEnabled)
    .length > 0;

const getIsCustomSectionEnabled = (
  incidentConfiguration: IncidentConfiguration,
  section: AdditionalInformationSection | undefined,
  additionalFieldOmitFn: SubtypeOmitFn,
  isHierarchyFieldGroupsEnabled: boolean
): boolean =>
  additionalFieldOmitFn(section) &&
  getHasRenderableFieldForSubtype(incidentConfiguration, section, additionalFieldOmitFn, isHierarchyFieldGroupsEnabled);

// `section` is required rather than optional here on purpose. Passing `section?.id` would hand `undefined`
// to getConfiguredFieldsValidations, whose `section = 'incident_details'` default would then validate the
// incident-details fields this dialog never renders — an unsubmittable dialog rather than a loud failure.
// Requiring the argument makes a missing section a compile error instead of a silent wrong-set validation.
const getCustomSectionValidationSchema = (
  incidentConfiguration: IncidentConfiguration,
  section: AdditionalInformationSection,
  additionalFieldOmitFn: SubtypeOmitFn | undefined,
  isHierarchyFieldGroupsEnabled: boolean
) => ({
  configured_fields_values: Yup.object().shape({
    ...(getConfiguredFieldsValidations(incidentConfiguration, section.id, additionalFieldOmitFn) as Record<
      string,
      Yup.AnySchema
    >),
    ...fieldGroupValidations(
      getFieldGroupsForSection(incidentConfiguration, section.id, additionalFieldOmitFn, isHierarchyFieldGroupsEnabled)
    ),
  }),
});

// Same reasoning as getCustomSectionValidationSchema: getConfiguredFieldsEmptyFormikInitialValues carries
// the identical `section = 'incident_details'` default, so `section` is required here too.
const getCustomSectionInitialValues = (
  incidentConfiguration: IncidentConfiguration,
  section: AdditionalInformationSection,
  additionalFieldOmitFn: SubtypeOmitFn | undefined,
  isHierarchyFieldGroupsEnabled: boolean
) => ({
  configured_fields_values: {
    ...getConfiguredFieldsEmptyFormikInitialValues(
      (get(incidentConfiguration, CONFIGURED_FIELDS_PATH) as readonly ConfiguredField[] | undefined) ?? EMPTY,
      section.id,
      additionalFieldOmitFn
    ),
    ...emptyFieldGroupFormikValues(
      getFieldGroupsForSection(incidentConfiguration, section.id, additionalFieldOmitFn, isHierarchyFieldGroupsEnabled)
    ),
  },
});

interface CustomSectionFragmentProps {
  sectionId: string;
  additionalFieldOmitFn?: SubtypeOmitFn;
  /** Claim page. Absent on FNOL. */
  claimId?: number;
}

const CustomSectionFragment: React.FC<CustomSectionFragmentProps> = ({ sectionId, additionalFieldOmitFn, claimId }) => {
  const { incidentConfiguration } = useIncidentConfiguration();
  const { userOrganization } = useCms();
  // useSafeFormikContext returns `useContext(FormikContext) || {}`, so it is always truthy — a
  // `formikContext ? … : {}` ternary can never take its else branch. Out of context it is the empty
  // object, whose `values` is undefined, so the fallback has to be on `values` itself to actually
  // produce the empty object this expects.
  const formikContext = useSafeFormikContext<Record<string, unknown>>();
  const values = 'values' in formikContext ? formikContext.values : {};
  const configurableFields = getConfiguredFieldsForSection(incidentConfiguration, sectionId).filter((field) =>
    additionalFieldOmitFn ? additionalFieldOmitFn(field) : true
  );
  const fieldGroups = getFieldGroupsForSection(
    incidentConfiguration,
    sectionId,
    additionalFieldOmitFn,
    isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS)
  );

  return (
    <>
      <Grid item xs={12}>
        <ConfiguredFields values={values} customFields={configurableFields} inline />
      </Grid>
      <SectionFieldGroupPickers fieldGroups={fieldGroups} claimId={claimId} />
    </>
  );
};

export {
  CustomSectionFragment,
  getConfiguredFieldsForSection,
  getCustomAdditionalInformationSections,
  getCustomSectionInitialValues,
  getCustomSectionPath,
  getCustomSectionValidationSchema,
  getFieldGroupsForSection,
  getHasRenderableFieldForSubtype,
  getIsCustomSectionActive,
  getIsCustomSectionEnabled,
  getIsCustomSectionMandatory,
};
export type { AdditionalInformationSection, ConfiguredField, SubtypeOmitFn };
